import React, { Suspense, useRef, useState, useEffect } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { OrbitControls, Environment, RoundedBox, ContactShadows } from "@react-three/drei";
import { RotateCw, RefreshCcw } from "lucide-react";

const isMobile = () => typeof window !== "undefined" && window.innerWidth < 768;

const CAM = { mask: 6, glove: 5.2, cap: 5 };
const SHADOW_Y = { mask: -1.4, glove: -1.6, cap: -1.55 };
const camDist = (k) => CAM[k] ?? 5.2;

/* ---- Bouffant cap (light blue non-woven) ---- */
function CapModel() {
  const fabric = { color: "#9fc6ea", roughness: 0.9, metalness: 0 };
  const band = { color: "#7fb0dc", roughness: 0.85 };
  return (
    <group position={[0, -0.35, 0]} scale={1.45}>
      <mesh>
        <sphereGeometry args={[1.1, 48, 32, 0, Math.PI * 2, 0, Math.PI / 2]} />
        <meshStandardMaterial {...fabric} side={2} />
      </mesh>
      {[0.28, 0.52, 0.76].map((t, i) => {
        const r = 1.1 * Math.cos((t * Math.PI) / 2) + 0.02;
        const y = 1.1 * Math.sin((t * Math.PI) / 2);
        return (
          <mesh key={i} position={[0, y, 0]} rotation={[Math.PI / 2, 0, 0]}>
            <torusGeometry args={[r, 0.02, 12, 48]} />
            <meshStandardMaterial {...band} />
          </mesh>
        );
      })}
      <mesh position={[0, 0.04, 0]} rotation={[Math.PI / 2, 0, 0]}>
        <torusGeometry args={[1.06, 0.08, 16, 64]} />
        <meshStandardMaterial {...band} />
      </mesh>
      <mesh position={[0, -0.02, 0]}>
        <cylinderGeometry args={[1.12, 1.12, 0.14, 48, 1, true]} />
        <meshStandardMaterial {...fabric} side={2} />
      </mesh>
    </group>
  );
}

function shape(kind) {
  if (kind === "mask") return <MaskModel />;
  if (kind === "cap") return <CapModel />;
  return <GloveModel />;
}

/* ---- Nitrile examination glove (blue-violet) ---- */
function GloveModel() {
  const mat = { color: "#6C63C7", roughness: 0.35, metalness: 0.05, clearcoat: 0.4, clearcoatRoughness: 0.5 };
  const fingers = [
    { x: -0.42, h: 1.05 }, { x: -0.14, h: 1.2 }, { x: 0.14, h: 1.15 }, { x: 0.42, h: 0.95 },
  ];
  return (
    <group rotation={[0.1, 0, 0]} position={[0, -0.2, 0]} scale={1.05}>
      {/* palm */}
      <RoundedBox args={[1.15, 1.35, 0.42]} radius={0.2} smoothness={5} position={[0, 0.35, 0]}>
        <meshPhysicalMaterial {...mat} />
      </RoundedBox>
      {/* cuff */}
      <mesh position={[0, -0.7, 0]}>
        <cylinderGeometry args={[0.62, 0.7, 0.9, 32, 1, true]} />
        <meshPhysicalMaterial {...mat} side={2} />
      </mesh>
      <mesh position={[0, -1.12, 0]}>
        <torusGeometry args={[0.66, 0.06, 16, 40]} />
        <meshPhysicalMaterial {...mat} />
      </mesh>
      {/* fingers */}
      {fingers.map((f, i) => (
        <mesh key={i} position={[f.x, 1.05 + f.h / 2 - 0.1, 0]}>
          <capsuleGeometry args={[0.135, f.h, 8, 16]} />
          <meshPhysicalMaterial {...mat} />
        </mesh>
      ))}
      {/* thumb */}
      <mesh position={[-0.72, 0.35, 0.05]} rotation={[0, 0, 0.9]}>
        <capsuleGeometry args={[0.15, 0.7, 8, 16]} />
        <meshPhysicalMaterial {...mat} />
      </mesh>
    </group>
  );
}

/* ---- 3-ply pleated face mask ---- */
function MaskModel() {
  const body = { color: "#8Fc0e8", roughness: 0.75, metalness: 0.0 };
  const pleat = { color: "#79b2e0", roughness: 0.8 };
  const loop = { color: "#dfeaf5", roughness: 0.6 };
  return (
    <group rotation={[0.05, 0, 0]} scale={1.0}>
      {/* mask body, slight bow */}
      <RoundedBox args={[3.2, 1.9, 0.12]} radius={0.14} smoothness={5}>
        <meshStandardMaterial {...body} />
      </RoundedBox>
      {/* pleats */}
      {[0.5, 0.0, -0.5].map((y, i) => (
        <RoundedBox key={i} args={[3.05, 0.16, 0.12]} radius={0.06} smoothness={4} position={[0, y, 0.09]}>
          <meshStandardMaterial {...pleat} />
        </RoundedBox>
      ))}
      {/* nose wire */}
      <RoundedBox args={[2.9, 0.1, 0.06]} radius={0.04} position={[0, 0.82, 0.1]}>
        <meshStandardMaterial color="#6fa6d6" roughness={0.5} />
      </RoundedBox>
      {/* ear loops */}
      <mesh position={[1.72, 0, 0]} rotation={[0, 0.5, 0]}>
        <torusGeometry args={[0.85, 0.045, 16, 60, Math.PI]} />
        <meshStandardMaterial {...loop} />
      </mesh>
      <mesh position={[-1.72, 0, 0]} rotation={[0, -0.5, Math.PI]}>
        <torusGeometry args={[0.85, 0.045, 16, 60, Math.PI]} />
        <meshStandardMaterial {...loop} />
      </mesh>
    </group>
  );
}

function AutoRotate({ enabled, groupRef }) {
  useFrame((_, delta) => {
    if (enabled && groupRef.current) groupRef.current.rotation.y += delta * 0.5;
  });
  return null;
}

function Model({ kind, controlsRef }) {
  const ref = useRef();
  const [interacted, setInteracted] = useState(false);
  const camZ = camDist(kind);
  return (
    <>
      <ambientLight intensity={0.7} />
      <directionalLight position={[4, 6, 5]} intensity={1.5} />
      <directionalLight position={[-4, 2, -3]} intensity={0.5} color="#bcd8f0" />
      <group ref={ref}>
        {shape(kind)}
      </group>
      <AutoRotate enabled={!interacted} groupRef={ref} />
      <ContactShadows position={[0, SHADOW_Y[kind] ?? -1.55, 0]} opacity={0.35} scale={8} blur={2.6} far={4} />
      <Environment preset="studio" />
      <OrbitControls
        ref={controlsRef}
        enableZoom enablePan={false}
        minDistance={camZ - 1.4} maxDistance={camZ + 1.6}
        minPolarAngle={Math.PI / 3} maxPolarAngle={(2 * Math.PI) / 3}
        zoomSpeed={0.6}
        onStart={() => setInteracted(true)}
      />
    </>
  );
}

export default function Product360({ kind, image, alt }) {
  const [webgl, setWebgl] = useState(true);
  const controlsRef = useRef();
  useEffect(() => {
    try {
      const c = document.createElement("canvas");
      setWebgl(!!(c.getContext("webgl") || c.getContext("experimental-webgl")));
    } catch { setWebgl(false); }
  }, []);

  if (!webgl) {
    return <img src={image} alt={alt} className="max-h-[340px] w-auto object-contain mix-blend-multiply" />;
  }

  return (
    <div className="relative w-full h-full" data-testid="product-360">
      <Canvas camera={{ position: [0, 0, camDist(kind)], fov: 42 }} dpr={[1, 1.75]} gl={{ antialias: true, alpha: true }}>
        <Suspense fallback={null}>
          <Model kind={kind} controlsRef={controlsRef} />
        </Suspense>
      </Canvas>
      <button
        onClick={() => controlsRef.current?.reset()}
        data-testid="product-360-reset"
        className="absolute top-4 right-4 inline-flex items-center gap-1.5 bg-white/90 backdrop-blur border border-[#0A1128]/10 px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider text-[#0A1128] hover:text-[#1F6FEB] transition-colors"
      >
        <RefreshCcw size={13} /> Reset
      </button>
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 inline-flex items-center gap-2 bg-white/85 backdrop-blur px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider text-[#1F6FEB] pointer-events-none">
        <RotateCw size={13} /> Drag to rotate · scroll to zoom
      </div>
    </div>
  );
}
